import {
  createContext,
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';
import { Box, IconButton, Paper, Tooltip, type SxProps, type Theme } from '@mui/material';
import {
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
} from '@mui/icons-material';
import { ResponsiveContainer } from 'recharts';
import { useTranslation } from 'react-i18next';
import { useAppSocket } from '../../../providers/AppSocketContext';
import { DashboardSectionTitle } from './DashboardInfoTip';
import { subscribeDashboardWidgetFullscreen } from './dashboardWidgetFullscreen';

type DashboardWidgetContextValue = {
  isFullscreen: boolean;
};

const DashboardWidgetContext = createContext<DashboardWidgetContextValue | null>(null);

export const useDashboardWidget = () => useContext(DashboardWidgetContext);

export const useDashboardWidgetScrollHeight = (defaultMax: number | string = 400) => {
  const ctx = useDashboardWidget();
  return ctx?.isFullscreen ? '100%' : defaultMax;
};

type DashboardWidgetProps = {
  children: ReactNode;
  title?: string;
  helpKey?: string;
  subtitle?: string;
  header?: ReactNode;
  headerActions?: ReactNode;
  chartHeight?: number;
  scrollable?: boolean;
  fitContent?: boolean;
  sx?: SxProps<Theme>;
};

const DashboardWidget = ({
  children,
  title,
  helpKey,
  subtitle,
  header,
  headerActions,
  chartHeight = 300,
  scrollable = false,
  fitContent = false,
  sx,
}: DashboardWidgetProps) => {
  const { t } = useTranslation();
  const { appSettings } = useAppSocket();
  const themeColor = appSettings?.UI_THEME_PRIMARY?.value || '#8b1010';
  const containerRef = useRef<HTMLDivElement>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    return subscribeDashboardWidgetFullscreen((activeElement) => {
      const next = activeElement === containerRef.current;
      setIsFullscreen((prev) => (prev === next ? prev : next));
    });
  }, []);

  const handleFullscreenToggle = useCallback(async () => {
    if (!containerRef.current) return;

    try {
      if (document.fullscreenElement === containerRef.current) {
        await document.exitFullscreen();
      } else {
        await containerRef.current.requestFullscreen();
      }
    } catch (error) {
      console.error('Widget fullscreen error:', error);
    }
  }, []);

  const contextValue = useMemo(() => ({ isFullscreen }), [isFullscreen]);
  const hasBuiltInTitle = Boolean(title && helpKey);

  return (
    <DashboardWidgetContext.Provider value={contextValue}>
      <Paper
        ref={containerRef}
        sx={{
          p: 3,
          borderRadius: 3,
          boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
          ...(isFullscreen && {
            display: 'flex',
            flexDirection: 'column',
            bgcolor: 'background.default',
            borderRadius: 0,
            width: '100%',
            height: '100%',
            overflow: 'hidden',
            boxSizing: 'border-box',
          }),
          ...sx,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 1,
            mb: 2,
            flexShrink: 0,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {header ?? (
              hasBuiltInTitle && (
                <DashboardSectionTitle
                  title={title!}
                  helpKey={helpKey!}
                  subtitle={subtitle}
                  mb={0}
                />
              )
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
            {headerActions}
            <Tooltip
              title={
                isFullscreen
                  ? t('common:dashboard.exitFullscreen')
                  : t('common:dashboard.fullscreen')
              }
            >
              <IconButton
                size="small"
                onClick={handleFullscreenToggle}
                aria-label={
                  isFullscreen
                    ? t('common:dashboard.exitFullscreen')
                    : t('common:dashboard.fullscreen')
                }
                sx={{ color: themeColor }}
              >
                {isFullscreen ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <Box
          sx={{
            flex: isFullscreen ? 1 : undefined,
            minHeight: isFullscreen ? 0 : (fitContent || scrollable) ? undefined : chartHeight,
            height: isFullscreen ? undefined : (fitContent || scrollable) ? undefined : chartHeight,
            width: '100%',
            display: isFullscreen ? 'flex' : 'block',
            flexDirection: isFullscreen ? 'column' : undefined,
            overflow: scrollable || isFullscreen ? 'auto' : 'hidden',
          }}
        >
          {children}
        </Box>
      </Paper>
    </DashboardWidgetContext.Provider>
  );
};

type DashboardSizedChartProps = {
  height: number;
  dynamicHeight?: number;
  children: ReactElement;
};

export const DashboardSizedChart = memo(({
  height,
  dynamicHeight,
  children,
}: DashboardSizedChartProps) => {
  const ctx = useDashboardWidget();
  const staticHeight = dynamicHeight != null
    ? Math.max(height, dynamicHeight)
    : height;

  if (ctx?.isFullscreen) {
    return (
      <Box sx={{ flex: 1, minHeight: 0, width: '100%' }}>
        <ResponsiveContainer width="100%" height="100%">
          {children}
        </ResponsiveContainer>
      </Box>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={staticHeight}>
      {children}
    </ResponsiveContainer>
  );
});

DashboardSizedChart.displayName = 'DashboardSizedChart';

export default memo(DashboardWidget);